/**
 * Centralized Formatting Utilities
 *
 * Provides consistent formatting functions for dates, currency, phone numbers,
 * percentages, and empty values across the platform.
 */

/**
 * Format a date value
 */
export function formatDate(date: Date | string | undefined | null, format: 'short' | 'medium' | 'long' | 'iso' | 'relative' = 'short'): string {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  switch (format) {
    case 'short':
      return dateObj.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    case 'medium':
      return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    case 'long':
      return dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    case 'iso':
      return dateObj.toISOString().split('T')[0];
    case 'relative': {
      const now = new Date();
      const diffMs = now.getTime() - dateObj.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays === -1) return 'Tomorrow';
      if (diffDays > 0 && diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 0 && diffDays > -7) return `In ${Math.abs(diffDays)} days`;
      if (diffDays >= 7 && diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays >= 30 && diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      if (diffDays >= 365) return `${Math.floor(diffDays / 365)} years ago`;
      return formatDate(date, 'short');
    }
    default:
      return dateObj.toLocaleDateString();
  }
}

/**
 * Normalize a numeric value from Prisma (number or bigint) to number for display/math.
 */
export function toDisplayNumber(value: number | bigint | undefined | null): number {
  if (value == null) return 0;
  return typeof value === 'bigint' ? Number(value) : value;
}

/**
 * Format a currency amount
 */
export function formatCurrency(
  amount: number | bigint | undefined | null,
  options: {
    showCents?: boolean;
    currency?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    isCents?: boolean;
  } = {}
): string {
  if (amount === undefined || amount === null) {
    return '$0.00';
  }
  const n = typeof amount === 'bigint' ? Number(amount) : amount;
  if (isNaN(n)) {
    return '$0.00';
  }

  const {
    showCents = true,
    currency = 'USD',
    minimumFractionDigits = showCents ? 2 : 0,
    maximumFractionDigits = showCents ? 2 : 0,
    isCents = true,
  } = options;

  const value = isCents ? n / 100 : n;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

/**
 * Format a phone number
 */
export function formatPhone(phone: string | undefined | null, format: 'formatted' | 'raw' = 'formatted'): string {
  if (!phone) return 'N/A';

  const digits = phone.replaceAll(/\D/g, '');

  if (digits.length === 0) return 'N/A';

  if (format === 'raw') {
    return digits;
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11) {
    return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return phone;
}

/**
 * Format a percentage value
 */
export function formatPercentage(
  value: number | undefined | null,
  decimals: number = 0,
  asDecimal: boolean = true
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '0%';
  }

  const percentage = asDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Format an empty/null value with a placeholder
 */
export function formatEmpty(value: unknown, placeholder: string = 'N/A'): string {
  if (value === null || value === undefined || value === '') {
    return placeholder;
  }
  return String(value);
}

/**
 * Format a number with thousand separators
 */
export function formatNumber(value: number | undefined | null, decimals: number = 0): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '0';
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null || bytes === 0) {
    return '0 B';
  }

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Format a duration in seconds to human-readable format
 */
export function formatDuration(seconds: number | undefined | null): string {
  if (seconds === undefined || seconds === null || isNaN(seconds) || seconds < 0) {
    return '0s';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
}
